import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchLanguages,
  createLanguage,
  updateLanguage,
  deleteLanguage,
} from '../../features/Languages/languageSlice';
import { Button, Table, Modal, Form, Input, Space, Popconfirm, message } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';

const Languages = () => {
  const dispatch = useDispatch();
  const { languages = [], loading } = useSelector((state) => state.languages);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingLanguage, setEditingLanguage] = useState(null);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    dispatch(fetchLanguages());
  }, [dispatch]);

  const handleAdd = () => {
    setEditingLanguage(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const filteredLanguages = languages.filter((lang) =>
    lang.name.toLowerCase().includes(searchText.toLowerCase()) ||
    lang.code.toLowerCase().includes(searchText.toLowerCase())
  );

  const handleEdit = (language) => {
    setEditingLanguage(language);
    form.setFieldsValue(language);
    setIsModalVisible(true);
  };

  const handleDelete = (id) => {
    dispatch(deleteLanguage(id))
      .unwrap()
      .then(() => message.success('Language deleted successfully'))
      .catch(() => message.error('Failed to delete language'));
  };

  // Helper function to get only changed fields
  const getChangedFields = (newValues, originalValues) => {
    const changed = {};
    Object.keys(newValues).forEach((key) => {
      if (newValues[key] !== originalValues[key]) {
        changed[key] = newValues[key];
      }
    });
    return changed;
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingLanguage) {
        // Compute the diff between the current values and the original language.
        const diff = getChangedFields(values, editingLanguage);
        if (Object.keys(diff).length === 0) {
          message.info('No changes detected.');
          return;
        }
        await dispatch(updateLanguage({ id: editingLanguage.id, ...diff })).unwrap();
        message.success('Language updated successfully');
      } else {
        await dispatch(createLanguage(values)).unwrap();
        message.success('Language created successfully');
      }
      setIsModalVisible(false);
    } catch (error) {
      message.error(error.message || 'An error occurred');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this language?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="link" icon={<DeleteOutlined />} danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="users-page">
      <h3 className='user-title'>Languages Management</h3>
      <Button className='add-button' type="primary" style={{ marginBottom: 16 }} onClick={handleAdd}>
        Add Language
      </Button>
      <Input.Search
        placeholder="Search languages"
        value={searchText}
        onChange={(e) => handleSearch(e.target.value)}
        style={{ marginBottom: 16 }}
      />
      <Table
        dataSource={filteredLanguages}
        columns={columns}
        loading={loading}
        rowKey="id"
        scroll={{ x: true }}
      />

      <Modal
        title={editingLanguage ? 'Edit Language' : 'Add Language'}
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={handleSubmit}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="code" label="Code" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Languages;
